import React from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Button,
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  InputGroup,
  Input,
  InputRightElement,
  useToast,
  useDisclosure,
  useColorModeValue,
  useClipboard,
  Spinner,
  Code,
  Select,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useMobile } from "./hooks";
import {
  getWebhookEndpoint,
  deleteWebhookEndpoint,
  rotateWebhookSecret,
  testWebhookEndpoint,
  getWebhookEventTypes,
  getWebhookDelivery,
  retryWebhookDelivery,
  updateWebhookEndpoint,
} from "./api";
import { WebhookEndpoint, WebhookDelivery } from "./types";
import { useWebhookDeliveries } from "./useWebhookDeliveries";
import { CreateWebhookModal } from "./CreateWebhookModal";

export const WebhookDetails = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const isSmallScreen = useMobile();
  const deleteDisclosure = useDisclosure();
  const rotateDisclosure = useDisclosure();
  const toggleDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();

  const [isToggling, setIsToggling] = React.useState(false);

  const [endpoint, setEndpoint] = React.useState<WebhookEndpoint | null>(null);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [sportFilter, setSportFilter] = React.useState("");
  const [sports, setSports] = React.useState<string[]>([]);
  const { deliveries, nextCursor, isLoading: isDeliveriesLoading, isLoadingMore, error: deliveriesError, fetchDeliveries } = useWebhookDeliveries(id, statusFilter, sportFilter);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isRotating, setIsRotating] = React.useState(false);
  const [isTesting, setIsTesting] = React.useState(false);
  const [newSecret, setNewSecret] = React.useState<string | null>(null);
  const [expandedDelivery, setExpandedDelivery] = React.useState<number | null>(
    null
  );
  const [expandedPayloads, setExpandedPayloads] = React.useState<
    Record<number, WebhookDelivery>
  >({});
  const [loadingPayload, setLoadingPayload] = React.useState<number | null>(
    null
  );
  const { onCopy, hasCopied, setValue: setClipboardValue } = useClipboard("");

  const bgColor = useColorModeValue("gray.50", "transparent");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const codeBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const dividerColor = useColorModeValue("gray.100", "gray.700");

  const fetchEndpoint = React.useCallback(async () => {
    if (!id) return;
    try {
      const ep = await getWebhookEndpoint(id);
      setEndpoint(ep);
    } catch {
      toast({
        title: "Endpoint not found",
        status: "error",
        isClosable: true,
      });
      navigate("/webhooks");
    }
  }, [id, toast, navigate]);

  React.useEffect(() => {
    setIsLoading(true);
    fetchEndpoint().finally(() => setIsLoading(false));
  }, [fetchEndpoint]);

  React.useEffect(() => {
    getWebhookEventTypes().then((events) => {
      setSports(Array.from(new Set(events.map((event) => event.sport))).sort());
    }).catch(() => {
      toast({ title: "Unable to load sport filters", status: "error", isClosable: true });
    });
  }, [toast]);

  React.useEffect(() => {
    setExpandedDelivery(null);
    setExpandedPayloads({});
  }, [id, statusFilter, sportFilter]);

  const handleToggleActive = async () => {
    if (!id || !endpoint) return;
    setIsToggling(true);
    try {
      const updated = await updateWebhookEndpoint(id, { active: !endpoint.active });
      setEndpoint(updated);
      toggleDisclosure.onClose();
      toast({
        title: updated.active ? "Endpoint enabled" : "Endpoint disabled",
        status: "success",
        isClosable: true,
        duration: 3000,
      });
    } catch {
      toast({
        title: "Failed to update endpoint",
        status: "error",
        isClosable: true,
      });
    }
    setIsToggling(false);
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteWebhookEndpoint(id);
      toast({
        title: "Endpoint deleted",
        status: "success",
        isClosable: true,
        duration: 3000,
      });
      navigate("/webhooks");
    } catch {
      toast({
        title: "Failed to delete endpoint",
        status: "error",
        isClosable: true,
      });
    }
    setIsDeleting(false);
  };

  const handleRotateSecret = async () => {
    if (!id) return;
    setIsRotating(true);
    try {
      const updated = await rotateWebhookSecret(id);
      setNewSecret(updated.secret);
      setClipboardValue(updated.secret);
      setEndpoint(updated);
    } catch {
      toast({
        title: "Failed to rotate secret",
        status: "error",
        isClosable: true,
      });
    }
    setIsRotating(false);
  };

  const handleTest = async () => {
    if (!id) return;
    setIsTesting(true);
    try {
      const result = await testWebhookEndpoint(id);
      if (result.success) {
        toast({
          title: "Test event sent",
          description: `Response status: ${result.status}`,
          status: "success",
          isClosable: true,
          duration: 5000,
        });
      } else {
        toast({
          title: "Test event failed",
          description: result.error || `Status: ${result.status}`,
          status: "warning",
          isClosable: true,
          duration: 5000,
        });
      }
    } catch {
      toast({
        title: "Failed to send test event",
        status: "error",
        isClosable: true,
      });
    }
    setIsTesting(false);
  };

  const handleRetry = async (deliveryId: number) => {
    try {
      await retryWebhookDelivery(deliveryId);
      toast({
        title: "Delivery queued for retry",
        status: "success",
        isClosable: true,
        duration: 3000,
      });
      fetchDeliveries();
    } catch {
      toast({
        title: "Failed to retry delivery",
        status: "error",
        isClosable: true,
      });
    }
  };

  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "green";
      case "pending":
        return "yellow";
      case "failed":
        return "orange";
      case "exhausted":
        return "red";
      default:
        return "gray";
    }
  };

  if (isLoading) {
    return (
      <Flex minH="100vh" w="100%" bg={bgColor} justify="center" pt={20}>
        <Spinner size="lg" color="brand.400" />
      </Flex>
    );
  }

  if (!endpoint) return null;

  return (
    <Box minH="100vh" w="100%" bg={bgColor} pt={8} pb={20}>
      <Container
        maxW={isSmallScreen ? "container.sm" : "container.lg"}
        px={6}
      >
        <VStack spacing={6} align="stretch">
          {/* Back button */}
          <Button
            variant="ghost"
            leftIcon={<ArrowBackIcon />}
            onClick={() => navigate("/webhooks")}
            alignSelf="flex-start"
            size="sm"
          >
            Back to Webhooks
          </Button>

          {/* Header */}
          <Flex
            justify="space-between"
            align={isSmallScreen ? "flex-start" : "center"}
            direction={isSmallScreen ? "column" : "row"}
            gap={3}
          >
            <VStack align="flex-start" spacing={1}>
              <HStack spacing={2}>
                <Heading size="md" isTruncated maxW="500px">
                  {endpoint.description || endpoint.url}
                </Heading>
                <Badge colorScheme={endpoint.active ? "green" : "gray"} fontSize="xs">
                  {endpoint.active ? "Active" : "Disabled"}
                </Badge>
              </HStack>
              {endpoint.description && (
                <Text fontSize="sm" color="gray.500" isTruncated maxW="500px">
                  {endpoint.url}
                </Text>
              )}
            </VStack>
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                rightIcon={<ChevronDownIcon />}
              >
                Actions
              </MenuButton>
              <MenuList>
                <MenuItem onClick={editDisclosure.onOpen}>
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={handleTest}
                  isDisabled={isTesting}
                >
                  {isTesting ? "Sending..." : "Send Test"}
                </MenuItem>
                <MenuItem onClick={toggleDisclosure.onOpen}>
                  {endpoint.active ? "Disable" : "Enable"}
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  color="red.400"
                  onClick={deleteDisclosure.onOpen}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>

          <Tabs variant="enclosed" colorScheme="brand">
            <TabList>
              <Tab>Overview</Tab>
              <Tab>Deliveries</Tab>
            </TabList>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel px={0} pt={6}>
                <VStack spacing={4} align="stretch">
                  <Card
                    p={5}
                    borderRadius="lg"
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={cardBorder}
                  >
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          color={labelColor}
                          mb={1}
                        >
                          URL
                        </Text>
                        <Code fontSize="sm" p={2} borderRadius="md" w="100%">
                          {endpoint.url}
                        </Code>
                      </Box>

                      <Divider />

                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          color={labelColor}
                          mb={1}
                        >
                          Created
                        </Text>
                        <Text fontSize="sm">
                          {new Date(endpoint.created_at).toLocaleDateString()}
                        </Text>
                      </Box>

                      <Divider />

                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          color={labelColor}
                          mb={2}
                        >
                          Event Types
                        </Text>
                        <Accordion allowMultiple>
                          {Object.entries(
                            endpoint.event_types.reduce((groups: Record<string, string[]>, et) => {
                              const sport = et.split('.')[0]?.toUpperCase() || 'OTHER';
                              if (!groups[sport]) groups[sport] = [];
                              groups[sport].push(et);
                              return groups;
                            }, {})
                          ).map(([sport, events]) => (
                            <AccordionItem
                              key={sport}
                              border="none"
                              borderBottom="1px solid"
                              borderColor={dividerColor}
                              _last={{ borderBottom: "none" }}
                            >
                              <AccordionButton
                                px={2}
                                py={2}
                                borderRadius="md"
                                _hover={{ bg: hoverBg }}
                              >
                                <HStack flex="1" spacing={2}>
                                  <Text fontSize="sm" fontWeight="600" textTransform="uppercase">
                                    {sport}
                                  </Text>
                                  <Badge
                                    colorScheme="brand"
                                    variant="subtle"
                                    fontSize="xs"
                                    borderRadius="full"
                                    px={2}
                                  >
                                    {events.length}
                                  </Badge>
                                </HStack>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel px={2} pb={3} pt={1}>
                                <VStack align="stretch" spacing={1}>
                                  {events.map((et) => (
                                    <Text
                                      key={et}
                                      fontSize="sm"
                                      fontFamily="mono"
                                      color={labelColor}
                                      py={0.5}
                                    >
                                      {et.split('.').slice(1).join('.')}
                                    </Text>
                                  ))}
                                </VStack>
                              </AccordionPanel>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </Box>

                      {endpoint.consecutive_failures > 0 && (
                        <>
                          <Divider />
                          <Box>
                            <Text
                              fontSize="sm"
                              fontWeight="500"
                              color="orange.400"
                            >
                              Consecutive Failures:{" "}
                              {endpoint.consecutive_failures}
                            </Text>
                            {endpoint.consecutive_failures >= 2 && (
                              <Text fontSize="sm" color="red.400">
                                Endpoint auto-disabled after 2 consecutive
                                failures.
                              </Text>
                            )}
                          </Box>
                        </>
                      )}

                      <Divider />

                      <Flex justify="space-between" align="center">
                        <Box>
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            color={labelColor}
                            mb={1}
                          >
                            Signing Secret
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Signature verification is optional but recommended
                          </Text>
                        </Box>
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="orange"
                          onClick={rotateDisclosure.onOpen}
                        >
                          Rotate Secret
                        </Button>
                      </Flex>
                    </VStack>
                  </Card>
                </VStack>
              </TabPanel>

              {/* Deliveries Tab */}
              <TabPanel px={0} pt={6}>
                <VStack spacing={4} align="stretch">
                  <Flex gap={3} wrap="wrap">
                    <Select
                      aria-label="Filter deliveries by sport"
                      size="sm"
                      w="200px"
                      value={sportFilter}
                      onChange={(e) => setSportFilter(e.target.value)}
                      placeholder="All sports"
                    >
                      {sports.map((sport) => <option key={sport} value={sport}>{sport.toUpperCase()}</option>)}
                    </Select>
                    <Select
                      aria-label="Filter deliveries by status"
                      size="sm"
                      w="200px"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      placeholder="All statuses"
                    >
                      <option value="pending">Pending</option>
                      <option value="delivered">Delivered</option>
                      <option value="failed">Failed</option>
                      <option value="exhausted">Exhausted</option>
                    </Select>
                  </Flex>

                  {deliveriesError && (
                    <Flex role="alert" gap={3} align="center">
                      <Text>{deliveriesError}</Text>
                      <Button size="sm" onClick={() => fetchDeliveries(nextCursor)} isLoading={isDeliveriesLoading || isLoadingMore}>Try again</Button>
                    </Flex>
                  )}
                  {isDeliveriesLoading ? (
                    <Flex justify="center" py={8}>
                      <Spinner size="md" color="brand.400" />
                    </Flex>
                  ) : deliveries.length === 0 ? (
                    <Card
                      p={6}
                      borderRadius="lg"
                      bg={cardBg}
                      borderWidth="1px"
                      borderColor={cardBorder}
                      textAlign="center"
                    >
                      <Text color="gray.500">{deliveriesError ? "Deliveries could not be loaded" : statusFilter || sportFilter ? "No deliveries match these filters" : "No deliveries yet"}</Text>
                    </Card>
                  ) : (
                    <>
                      {deliveries.map((d) => (
                        <Card
                          key={d.id}
                          p={4}
                          borderRadius="lg"
                          bg={cardBg}
                          borderWidth="1px"
                          borderColor={cardBorder}
                          cursor="pointer"
                          onClick={async () => {
                            if (expandedDelivery === d.id) {
                              setExpandedDelivery(null);
                              return;
                            }
                            setExpandedDelivery(d.id);
                            if (!expandedPayloads[d.id]) {
                              setLoadingPayload(d.id);
                              try {
                                const full = await getWebhookDelivery(d.id);
                                setExpandedPayloads((prev) => ({
                                  ...prev,
                                  [d.id]: full,
                                }));
                              } catch {
                                toast({
                                  title: "Failed to load event payload",
                                  status: "error",
                                  isClosable: true,
                                });
                              }
                              setLoadingPayload((prev) =>
                                prev === d.id ? null : prev
                              );
                            }
                          }}
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            wrap="wrap"
                            gap={2}
                          >
                            <HStack spacing={3}>
                              <Badge
                                colorScheme={getDeliveryStatusColor(d.status)}
                                fontSize="xs"
                              >
                                {d.status}
                              </Badge>
                              {d.event && (
                                <Text fontSize="sm" fontWeight="500">
                                  {d.event.type}
                                </Text>
                              )}
                            </HStack>
                            <HStack spacing={3}>
                              {d.last_response_status && (
                                <Badge
                                  variant="outline"
                                  colorScheme={
                                    d.last_response_status >= 200 &&
                                    d.last_response_status < 300
                                      ? "green"
                                      : "red"
                                  }
                                  fontSize="xs"
                                >
                                  HTTP {d.last_response_status}
                                </Badge>
                              )}
                              <Text fontSize="xs" color="gray.500">
                                {d.attempts}/{d.max_attempts} attempts
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {new Date(d.created_at).toLocaleString()}
                              </Text>
                            </HStack>
                          </Flex>

                          {/* Expanded details */}
                          {expandedDelivery === d.id && (
                            <Box mt={3} pt={3} borderTopWidth="1px">
                              <VStack align="stretch" spacing={2}>
                                {d.duration_ms !== null && (
                                  <Text fontSize="xs" color="gray.500">
                                    Duration: {d.duration_ms}ms
                                  </Text>
                                )}
                                {d.delivered_at && (
                                  <Text fontSize="xs" color="gray.500">
                                    Delivered:{" "}
                                    {new Date(d.delivered_at).toLocaleString()}
                                  </Text>
                                )}
                                {d.last_error && (
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="500"
                                      color="red.400"
                                      mb={1}
                                    >
                                      Error
                                    </Text>
                                    <Code
                                      fontSize="xs"
                                      p={2}
                                      borderRadius="md"
                                      bg={codeBg}
                                      w="100%"
                                      display="block"
                                      whiteSpace="pre-wrap"
                                    >
                                      {d.last_error}
                                    </Code>
                                  </Box>
                                )}
                                {d.last_response_body && (
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="500"
                                      color={labelColor}
                                      mb={1}
                                    >
                                      Response Body
                                    </Text>
                                    <Code
                                      fontSize="xs"
                                      p={2}
                                      borderRadius="md"
                                      bg={codeBg}
                                      w="100%"
                                      display="block"
                                      whiteSpace="pre-wrap"
                                      maxH="200px"
                                      overflowY="auto"
                                    >
                                      {d.last_response_body}
                                    </Code>
                                  </Box>
                                )}
                                {d.event && (
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="500"
                                      color={labelColor}
                                      mb={1}
                                    >
                                      Event Payload
                                    </Text>
                                    {loadingPayload === d.id ? (
                                      <Spinner size="sm" color="brand.400" />
                                    ) : expandedPayloads[d.id]?.event?.payload ? (
                                      <Code
                                        fontSize="xs"
                                        p={2}
                                        borderRadius="md"
                                        bg={codeBg}
                                        w="100%"
                                        display="block"
                                        whiteSpace="pre-wrap"
                                        maxH="200px"
                                        overflowY="auto"
                                      >
                                        {JSON.stringify(
                                          expandedPayloads[d.id].event!.payload,
                                          null,
                                          2
                                        )}
                                      </Code>
                                    ) : !loadingPayload ? (
                                      <Text fontSize="xs" color="gray.500">
                                        No payload available
                                      </Text>
                                    ) : null}
                                  </Box>
                                )}
                                {(d.status === "failed" ||
                                  d.status === "exhausted") && (
                                  <Button
                                    size="xs"
                                    colorScheme="brand"
                                    variant="outline"
                                    alignSelf="flex-start"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRetry(d.id);
                                    }}
                                  >
                                    Retry
                                  </Button>
                                )}
                              </VStack>
                            </Box>
                          )}
                        </Card>
                      ))}

                      {nextCursor && (
                        <Button
                          variant="outline"
                          size="sm"
                          isLoading={isLoadingMore}
                          isDisabled={isLoadingMore}
                          onClick={() => fetchDeliveries(nextCursor)}
                        >
                          Load More
                        </Button>
                      )}
                    </>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>

      {/* Toggle Active Confirmation Modal */}
      <Modal isOpen={toggleDisclosure.isOpen} onClose={toggleDisclosure.onClose}>
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader>
            {endpoint.active ? "Disable Endpoint" : "Enable Endpoint"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              {endpoint.active
                ? "Are you sure you want to disable this endpoint? Webhook events will no longer be delivered until it is re-enabled."
                : "Are you sure you want to enable this endpoint? Webhook events will start being delivered immediately."}
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack justify="space-between" w="100%">
              <Button variant="ghost" onClick={toggleDisclosure.onClose}>
                Cancel
              </Button>
              <Button
                colorScheme={endpoint.active ? "red" : "green"}
                isLoading={isToggling}
                onClick={handleToggleActive}
              >
                {endpoint.active ? "Disable" : "Enable"}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteDisclosure.isOpen} onClose={deleteDisclosure.onClose}>
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader>Delete Endpoint</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this webhook endpoint? This action
              cannot be undone. All delivery history will be lost.
            </Text>
          </ModalBody>
          <ModalFooter>
            <HStack justify="space-between" w="100%">
              <Button variant="ghost" onClick={deleteDisclosure.onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isLoading={isDeleting}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Webhook Modal */}
      <CreateWebhookModal
        isOpen={editDisclosure.isOpen}
        onClose={editDisclosure.onClose}
        onCreated={fetchEndpoint}
        endpoint={endpoint}
      />

      {/* Rotate Secret Modal */}
      <Modal
        isOpen={rotateDisclosure.isOpen}
        onClose={() => {
          rotateDisclosure.onClose();
          setNewSecret(null);
        }}
      >
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader>
            {newSecret ? "New Signing Secret" : "Rotate Signing Secret"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {newSecret ? (
              <VStack spacing={4} align="stretch">
                <Text>
                  Your signing secret has been rotated. Copy the new secret
                  below — it will not be shown again.
                </Text>
                <InputGroup>
                  <Input
                    value={newSecret}
                    isReadOnly
                    fontFamily="mono"
                    fontSize="sm"
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={onCopy}
                      colorScheme={hasCopied ? "green" : "brand"}
                    >
                      {hasCopied ? "Copied" : "Copy"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </VStack>
            ) : (
              <Text>
                Are you sure you want to rotate the signing secret? The current
                secret will be{" "}
                <Text as="span" fontWeight="bold">
                  permanently invalidated
                </Text>
                . All webhook consumers will need to update their verification
                code.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack justify="space-between" w="100%">
              {newSecret ? (
                <Button
                  colorScheme="brand"
                  onClick={() => {
                    rotateDisclosure.onClose();
                    setNewSecret(null);
                  }}
                  w="100%"
                >
                  Done
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={rotateDisclosure.onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="orange"
                    isLoading={isRotating}
                    onClick={handleRotateSecret}
                  >
                    Rotate Secret
                  </Button>
                </>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
